import { Box, FormControl, MenuItem, Select, TextField, Typography } from "@mui/material"
import type { FC } from "react"
import { Controller, useFormContext } from "react-hook-form"

import {
  FIELD_ERROR_CLASSES,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
  TEXT_FIELD_STYLING_PROPS,
} from "@/constants/forms"
import { CompanyDepartment } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import type { ContactGeneralInfoFormValues } from "@/schemas/contactFormSchema"
import { formatLocationAddress } from "@/utils/utils"

export interface ContactGeneralInfoFormProps {
  companyId?: string
}

export const ContactGeneralInfoForm: FC<ContactGeneralInfoFormProps> = ({ companyId }) => {
  const { control } = useFormContext<ContactGeneralInfoFormValues>()
  const { locations } = useGetLocations(companyId)

  return (
    <form>
      <Typography variant='body1' fontWeight={700} className='leading-6 tracking-[0.15px] text-gray-700'>
        General information
      </Typography>
      <Box>
        <Controller
          name='firstName'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box className='mt-6'>
              <label htmlFor='contact-first-name' className={FIELD_LABEL_CLASSES}>
                First Name<span className='text-red-600'>*</span>
              </label>
              <TextField
                id='contact-first-name'
                {...field}
                {...TEXT_FIELD_STYLING_PROPS}
                error={!!error}
                placeholder='First Name'
              />
              {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
            </Box>
          )}
        />
        <Controller
          name='lastName'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box className='mt-6'>
              <label htmlFor='contact-last-name' className={FIELD_LABEL_CLASSES}>
                Last Name<span className='text-red-600'>*</span>
              </label>
              <TextField
                id='contact-last-name'
                {...field}
                {...TEXT_FIELD_STYLING_PROPS}
                error={!!error}
                placeholder='Last Name'
              />
              {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
            </Box>
          )}
        />
        <Controller
          name='companyDepartment'
          control={control}
          render={({ field }) => (
            <FormControl fullWidth className='mt-6'>
              <label htmlFor='contact-company-department' className={FIELD_LABEL_CLASSES}>
                Company Department
              </label>
              <Select id='contact-company-department' {...field} {...SELECT_STYLING_PROPS}>
                {Object.keys(CompanyDepartment).map((companyDepartment) => (
                  <MenuItem key={companyDepartment} value={companyDepartment}>
                    {companyDepartment}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        <Controller
          name='title'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box className='mt-6'>
              <label htmlFor='contact-title' className={FIELD_LABEL_CLASSES}>
                Title
              </label>
              <TextField id='contact-title' {...field} {...TEXT_FIELD_STYLING_PROPS} placeholder='Title' />
              {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
            </Box>
          )}
        />
        <Controller
          name='physicalLocationId'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth className='mt-6'>
              <label htmlFor='contact-office-location' className={FIELD_LABEL_CLASSES}>
                Office Location<span className='text-red-600'>*</span>
              </label>
              <Select
                id='contact-office-location'
                {...field}
                {...SELECT_STYLING_PROPS}
                error={!!error}
                renderValue={() => {
                  if (!field.value) return null
                  const selectedOption = locations.find(({ locationId }) => locationId === field.value)
                  return selectedOption ? formatLocationAddress(selectedOption) : null
                }}
              >
                <MenuItem key={field.name + "-select-one"} value=''>
                  Select One
                </MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.locationId} value={location.locationId}>
                    {formatLocationAddress(location)}
                  </MenuItem>
                ))}
              </Select>
              {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
            </FormControl>
          )}
        />
      </Box>
    </form>
  )
}
